import { TextEditorAsset } from '../../types'

export const tiptapResizableNodeViewWrapper = '[data-resize-wrapper]'
export const tiptapResizableNodeViewHandle = '[data-resize-handle]'
export const textEditorAssetWrapper = 'texteditor-asset-image'

/**
 * Default size for images in the text editor (in pixels).
 */
export const DEFAULT_IMAGE_SIZE = 160

/**
 * Gets aspect ratio from asset metadata or node attributes.
 * Prefers asset dimensions, falls back to node attributes, defaults to 1:1 (square).
 */
export const getImageAspectRatio = (
  asset: TextEditorAsset | undefined,
  nodeAttrs: { width: number; height: number }
): number => {
  if (!asset && !nodeAttrs.width && !nodeAttrs.height) {
    return 1
  }

  return nodeAttrs.width / nodeAttrs.height
}

/**
 * Gets maximum width for images in the editor.
 * Calculates based on the editor container width minus padding.
 */
export const getMaxWidth = (editorDom: HTMLElement): number => {
  const editorContainer =
    editorDom.closest('.ProseMirror') || editorDom.parentElement
  return editorContainer ? editorContainer.clientWidth - 72 : Infinity
}

/**
 * Calculates minimum dimensions based on aspect ratio.
 * Ensures both width and height are at least DEFAULT_IMAGE_SIZE
 * which ensures the asset's info overlay is fully visible.
 */
export const getMinDimensions = (
  aspectRatio: number
): { width: number; height: number } => {
  // For landscape images (wider than tall), constrain height to DEFAULT_IMAGE_SIZE
  if (aspectRatio >= 1) {
    const height = DEFAULT_IMAGE_SIZE
    const width = Math.max(DEFAULT_IMAGE_SIZE, height * aspectRatio)
    return { width, height }
  }

  // For portrait images (taller than wide), constrain width to DEFAULT_IMAGE_SIZE
  const width = DEFAULT_IMAGE_SIZE
  const height = Math.max(DEFAULT_IMAGE_SIZE, width / aspectRatio)
  return { width, height }
}

/**
 * Calculates image display dimensions based on aspect ratio.
 * Constrains the larger dimension to DEFAULT_IMAGE_SIZE while maintaining aspect ratio.
 */
export const calculateImageDimensions = (
  assetWidth: number,
  assetHeight: number
): { width: number; height: number } => {
  const aspectRatio = assetWidth / assetHeight
  return getMinDimensions(aspectRatio)
}

/**
 * Calculates image transforms sizing for images in the editor.
 * Returns proportional width and height to maintain aspect ratio.
 * Defaults to 500px width which scales 2x for high DPI displays automatically.
 */
export const calculateResizedImageTransforms = (
  assetWidth: number,
  assetHeight: number,
  targetWidth: number = 500
): { width: number; height: number } => {
  const aspectRatio = assetWidth / assetHeight
  const height = Math.round(targetWidth / aspectRatio)
  return { width: targetWidth, height }
}
